import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { Box } from '@/components/Box';
import { Heading } from '@/components/Cms/Heading/Heading';
import type {
  ContentfulFaqWrapFragment,
  ContentfulHeadingFragment,
} from '@/graphql/contentful/api';
import { useIsMdDown } from '@/hooks/useMedia';
import { messages } from '@/messages/faq';
import { theme } from '@/theme';

import { FaqItem } from './components/FaqItem/FaqItem';
import type { FaqWrapProps } from './FaqWrap';

import * as S from './FaqWrap.styles';
export type FAQSectionProps = {
  collapse: ContentfulFaqWrapFragment['collapse'];
  faqListCollection: ContentfulFaqWrapFragment['faqListCollection'];
  isFromFaqWrapCategories: boolean;
  placeholderHeading: ContentfulHeadingFragment;
};
export const FAQSection = (props: FAQSectionProps) => {
  const [headingProps, setHeadingProps] = useState<ContentfulHeadingFragment>();
  const { formatMessage } = useIntl();
  const isMdDown = useIsMdDown();
  const {
    collapse,
    faqListCollection,
    isFromFaqWrapCategories,
    placeholderHeading,
  } = props;
  useEffect(() => {
    if (placeholderHeading) {
      setHeadingProps({
        ...placeholderHeading,
        textAlignment: isMdDown ? 'center' : placeholderHeading.textAlignment,
      });
    }
  }, [isMdDown]);
  const [showAllItems, setShowAllItems] = useState(!collapse);
  const displayedItems = showAllItems
    ? faqListCollection?.items
    : faqListCollection?.items?.slice(0, 3);
  const shouldShowMore =
    showAllItems === false &&
    faqListCollection?.items &&
    faqListCollection.items.length > 3;
  const handleShowMore = () => {
    setShowAllItems(true);
  };
  return (
    <S.Wrapper data-test-id='FAQSectionWrapper'>
      <S.Container $isFromFaqWrapCategories={isFromFaqWrapCategories}>
        {placeholderHeading && (
          <div
            id={
              placeholderHeading?.text
                ?.toLowerCase()
                .replace(/ /g, '-') as string
            }
          >
            <Heading
              Heading={headingProps}
              color={placeholderHeading?.mainColor ?? theme.colors.default}
              data-test-id='FAQSectionHeading'
            />
          </div>
        )}
        {displayedItems?.map((elem, index) => (
          <FaqItem
            key={elem?.sys?.id}
            data-test-id={`FAQSectionFaqItem-${index}`}
            faqAnswer={elem?.faqAnswer}
            faqQuestion={elem?.faqQuestion as string}
            isFromFaqWrapCategories={isFromFaqWrapCategories}
          />
        ))}
        {shouldShowMore && (
          <S.MoreResultsBox
            data-test-id='FAQSectionMoreResultsBox'
            onClick={handleShowMore}
          >
            <Box>
              <span>{formatMessage(messages.moreResults)}</span>
              <S.StyledIcon color='primary' name='chevronDown' />
            </Box>
          </S.MoreResultsBox>
        )}
      </S.Container>
    </S.Wrapper>
  );
};

export const renderContentfulFaqSection = (props: FaqWrapProps) => {
  const isFromFaqWrapCategories = props?.isFromFaqWrapCategories ?? false;
  const collapse = props?.collapse ?? true;
  const faqListCollection = props?.faqListCollection;
  const placeholderHeading =
    props?.placeholderHeading as ContentfulHeadingFragment;
  return (
    <FAQSection
      collapse={collapse}
      faqListCollection={faqListCollection}
      isFromFaqWrapCategories={isFromFaqWrapCategories}
      placeholderHeading={placeholderHeading}
    />
  );
};
